import { Badge, Button, Dialog, Select, type BadgeTone } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DUREE_SUPPORT_MAX_HEURES,
  useAccesSupport,
  useGestesAccesVenio,
  useMandatReprise,
  type AccesSupport,
  type DemandeSupport,
  type MandatReprise,
} from '../../api/accesVenio.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { useShellContext } from '../shell/context.js';
import styles from './AccesVenioPage.module.css';
import { CarteRepriseDonnees } from './CarteRepriseDonnees.js';

/** Ce que la boîte de confirmation demande de valider. */
type Geste =
  | { readonly type: 'accorderMandat'; readonly demande: MandatReprise }
  | { readonly type: 'revoquerMandat' }
  | {
      readonly type: 'accorderSupport';
      readonly demande: DemandeSupport;
      readonly dureeHeures: number;
    }
  | { readonly type: 'revoquerSupport' };

const DUREES_SUPPORT = [1, 4, 8, DUREE_SUPPORT_MAX_HEURES] as const;

const TON_ETAT: Record<string, BadgeTone> = {
  actif: 'available',
  accordee: 'available',
  en_attente: 'reserved',
  refuse: 'sold',
  refusee: 'sold',
};

/** Une demande dont la péremption est passée ne se tranche plus. */
function enVigueur(expireLe: string, maintenant: number): boolean {
  return new Date(expireLe).getTime() > maintenant;
}

/**
 * Réglages › Accès Venio : ce que Venio peut faire chez vous, et pour combien
 * de temps. Deux accords distincts — le mandat de reprise (import de données
 * par l'outil de Venio) et l'accès de support (ouvrir la régie pour dépanner).
 * La direction accorde, refuse ou retire, en personne : le serveur refuse
 * un jeton comme un accès de support (`console/mandat.ts`, `console/support.ts`).
 */
export function AccesVenioPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const direction = !galerie.support && galerieALaction(galerie, 'settings:team');
  const mandat = useMandatReprise(galerieSlug, { enabled: direction });
  const support = useAccesSupport(galerieSlug, { enabled: direction });
  const gestes = useGestesAccesVenio(galerieSlug);
  const [aConfirmer, setAConfirmer] = useState<Geste | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [durees, setDurees] = useState<Record<string, number>>({});
  // Accorder exige une vérification récente du second facteur : le geste
  // refusé attend ici d'être rejoué, une fois la vérification faite.
  const [aRejouer, setARejouer] = useState<Geste | null>(null);

  const langue = i18n.resolvedLanguage === 'en' ? 'en' : 'fr';
  // L'instant des dernières lectures : une échéance s'apprécie à la lecture.
  const maintenant = Math.max(mandat.dataUpdatedAt, support.dataUpdatedAt);
  const cle = 'regie:reglages.accesVenio';

  const date = (iso: string): string =>
    new Intl.DateTimeFormat(langue === 'en' ? 'en-GB' : 'fr-FR', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: 'Europe/Paris',
    }).format(new Date(iso));
  const jour = (iso: string): string =>
    new Intl.DateTimeFormat(langue === 'en' ? 'en-GB' : 'fr-FR', {
      dateStyle: 'long',
      timeZone: 'Europe/Paris',
    }).format(new Date(iso));
  const natures = (liste: readonly string[]): string =>
    liste.map((nature) => t(`${cle}.natures.${nature}`)).join(', ');

  const echec = (erreur: Error): void =>
    setMessage(estApiError(erreur) ? erreur.message : t('commun:erreurReseau'));
  const options = { onError: echec };

  function executer(geste: Geste): void {
    setMessage(null);
    const optionsAccord = {
      onError: (erreur: Error) => {
        if (estErreurStepUpRequis(erreur)) setARejouer(geste);
        else echec(erreur);
      },
    };
    switch (geste.type) {
      case 'accorderMandat':
        gestes.accorderMandat.mutate(geste.demande.id, optionsAccord);
        break;
      case 'revoquerMandat':
        gestes.revoquerMandat.mutate(undefined, options);
        break;
      case 'accorderSupport':
        gestes.accorderSupport.mutate(
          { id: geste.demande.id, dureeHeures: geste.dureeHeures },
          optionsAccord,
        );
        break;
      case 'revoquerSupport':
        gestes.revoquerSupport.mutate(undefined, options);
        break;
    }
  }

  const enCours = Object.values(gestes).some((geste) => geste.isPending);

  if (!direction) {
    return (
      <div className={styles.page} data-ui="acces-venio">
        <h1 className={styles.titre} data-ui="page-title">
          {t(`${cle}.titre`)}
        </h1>
        <p className={styles.note} role="note">
          {t(`${cle}.refus`)}
        </p>
      </div>
    );
  }

  const mandatActif = mandat.data?.actif ?? null;
  const demandesMandat = (mandat.data?.demandes ?? []).filter((demande) =>
    enVigueur(demande.expireLe, maintenant),
  );
  const historiqueMandat = (mandat.data?.historique ?? []).filter(
    (ligne) => ligne.id !== mandatActif?.id && !demandesMandat.some((d) => d.id === ligne.id),
  );
  const demandesSupport = (support.data?.demandes ?? []).filter(
    (demande) => demande.etat === 'en_attente' && enVigueur(demande.expireLe, maintenant),
  );
  const accesSupport: readonly AccesSupport[] = (support.data?.acces ?? []).filter(
    (acces) => acces.actif && enVigueur(acces.expireLe, maintenant),
  );
  const historiqueSupport = (support.data?.demandes ?? [])
    .filter((demande) => !demandesSupport.some((courante) => courante.id === demande.id))
    .slice(0, 5);

  const etatMandat = (ligne: MandatReprise): string =>
    ligne.etat === 'actif' && !enVigueur(ligne.expireLe, maintenant)
      ? t(`${cle}.etatsMandat.expire`)
      : t(`${cle}.etatsMandat.${ligne.etat}`);

  return (
    <div className={styles.page} data-ui="acces-venio">
      <h1 className={styles.titre} data-ui="page-title">
        {t(`${cle}.titre`)}
      </h1>
      <p className={styles.sousTitre}>{t(`${cle}.sousTitre`)}</p>
      {message ? (
        <p className={styles.alerte} role="alert">
          {message}
        </p>
      ) : null}

      <CarteRepriseDonnees galerieSlug={galerieSlug} />

      <section className={styles.carte} aria-labelledby="acces-venio-mandat">
        <h2 id="acces-venio-mandat" className={styles.titreSection}>
          {t(`${cle}.mandat.titre`)}
        </h2>
        <p className={styles.description}>{t(`${cle}.mandat.texte`)}</p>

        {mandat.isLoading ? (
          <p className={styles.vide}>{t('regie:commun.chargement')}</p>
        ) : mandat.isError ? (
          <p className={styles.alerte} role="alert">
            {estApiError(mandat.error) ? mandat.error.message : t('commun:erreurReseau')}
          </p>
        ) : (
          <>
            {demandesMandat.map((demande) => (
              <article
                key={demande.id}
                className={styles.demande}
                aria-labelledby={`demande-mandat-${demande.id}`}
              >
                <h3 id={`demande-mandat-${demande.id}`} className={styles.titreBloc}>
                  {t(`${cle}.mandat.demandeTitre`)}
                </h3>
                <dl className={styles.details}>
                  <div>
                    <dt>{t(`${cle}.mandat.natures`)}</dt>
                    <dd>{natures(demande.natures)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.confidentiels`)}</dt>
                    <dd>{t(`${cle}.mandat.${demande.confidentiels ? 'inclus' : 'exclus'}`)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.duree`)}</dt>
                    <dd>{t(`${cle}.mandat.jours`, { count: demande.dureeJours ?? 0 })}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.motif`)}</dt>
                    <dd>{demande.motif}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.demandePar`)}</dt>
                    <dd>
                      {t(`${cle}.mandat.demandeDetail`, {
                        email: demande.demandePar ?? '—',
                        date: date(demande.demandeLe),
                      })}
                    </dd>
                  </div>
                </dl>
                <p className={styles.discret}>
                  {t(`${cle}.mandat.demandeExpire`, { date: date(demande.expireLe) })}
                </p>
                <div className={styles.actions}>
                  <Button
                    disabled={enCours}
                    onClick={() => setAConfirmer({ type: 'accorderMandat', demande })}
                  >
                    {t(`${cle}.accorder`)}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={enCours}
                    onClick={() => {
                      setMessage(null);
                      gestes.refuserMandat.mutate(demande.id, options);
                    }}
                  >
                    {t(`${cle}.refuser`)}
                  </Button>
                </div>
              </article>
            ))}

            {mandatActif ? (
              <article className={styles.actif} aria-labelledby="mandat-actif">
                <h3 id="mandat-actif" className={styles.titreBloc}>
                  {t(`${cle}.mandat.enCours`)}
                  <Badge tone="available">{t(`${cle}.etatsMandat.actif`)}</Badge>
                </h3>
                <dl className={styles.details}>
                  <div>
                    <dt>{t(`${cle}.mandat.natures`)}</dt>
                    <dd>{natures(mandatActif.natures)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.confidentiels`)}</dt>
                    <dd>{t(`${cle}.mandat.${mandatActif.confidentiels ? 'inclus' : 'exclus'}`)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.echeance`)}</dt>
                    <dd>{date(mandatActif.expireLe)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.origine`)}</dt>
                    <dd>
                      {mandatActif.source === 'contrat'
                        ? t(`${cle}.mandat.origineContrat`, { reference: mandatActif.reference })
                        : t(`${cle}.mandat.origineGalerie`, {
                            email: mandatActif.accordePar ?? '—',
                          })}
                    </dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.motif`)}</dt>
                    <dd>{mandatActif.motif}</dd>
                  </div>
                </dl>
                <div className={styles.actions}>
                  <Button
                    variant="danger"
                    disabled={enCours}
                    onClick={() => setAConfirmer({ type: 'revoquerMandat' })}
                  >
                    {t(`${cle}.mandat.revoquer`)}
                  </Button>
                </div>
              </article>
            ) : demandesMandat.length === 0 ? (
              <p className={styles.vide}>{t(`${cle}.mandat.aucun`)}</p>
            ) : null}

            {historiqueMandat.length > 0 ? (
              <section aria-labelledby="mandat-historique">
                <h3 id="mandat-historique" className={styles.titreBloc}>
                  {t(`${cle}.historique`)}
                </h3>
                <ul className={styles.historique}>
                  {historiqueMandat.map((ligne) => (
                    <li key={ligne.id}>
                      <Badge tone={TON_ETAT[ligne.etat] ?? 'neutral'}>{etatMandat(ligne)}</Badge>
                      <span>{natures(ligne.natures)}</span>
                      <span className={styles.discret}>{jour(ligne.demandeLe)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </section>

      <section className={styles.carte} aria-labelledby="acces-venio-support">
        <h2 id="acces-venio-support" className={styles.titreSection}>
          {t(`${cle}.support.titre`)}
        </h2>
        <p className={styles.description}>{t(`${cle}.support.texte`)}</p>

        {support.isLoading ? (
          <p className={styles.vide}>{t('regie:commun.chargement')}</p>
        ) : support.isError ? (
          <p className={styles.alerte} role="alert">
            {estApiError(support.error) ? support.error.message : t('commun:erreurReseau')}
          </p>
        ) : (
          <>
            {demandesSupport.map((demande) => {
              const dureeHeures = durees[demande.id] ?? DUREE_SUPPORT_MAX_HEURES;
              return (
                <article
                  key={demande.id}
                  className={styles.demande}
                  aria-labelledby={`demande-support-${demande.id}`}
                >
                  <h3 id={`demande-support-${demande.id}`} className={styles.titreBloc}>
                    {t(`${cle}.support.demandeTitre`)}
                  </h3>
                  <dl className={styles.details}>
                    <div>
                      <dt>{t(`${cle}.support.demandePar`)}</dt>
                      <dd>
                        {t(`${cle}.support.demandeDetail`, {
                          email: demande.demandeurEmail ?? '—',
                          date: date(demande.demandeLe),
                        })}
                      </dd>
                    </div>
                    <div>
                      <dt>{t(`${cle}.support.role`)}</dt>
                      <dd>{t(`${cle}.support.roles.${demande.role}`)}</dd>
                    </div>
                    <div>
                      <dt>{t(`${cle}.mandat.motif`)}</dt>
                      <dd>{demande.motif}</dd>
                    </div>
                  </dl>
                  <div className={styles.dureeChamp}>
                    <span>{t(`${cle}.support.duree`)}</span>
                    <Select
                      aria-label={t(`${cle}.support.duree`)}
                      value={String(dureeHeures)}
                      onValueChange={(valeur) =>
                        setDurees((courantes) => ({ ...courantes, [demande.id]: Number(valeur) }))
                      }
                      options={DUREES_SUPPORT.map((heures) => ({
                        value: String(heures),
                        label: t(`${cle}.support.heures`, { count: heures }),
                      }))}
                    />
                  </div>
                  <p className={styles.discret}>
                    {t(`${cle}.support.demandeExpire`, { date: date(demande.expireLe) })}
                  </p>
                  <div className={styles.actions}>
                    <Button
                      disabled={enCours}
                      onClick={() =>
                        setAConfirmer({ type: 'accorderSupport', demande, dureeHeures })
                      }
                    >
                      {t(`${cle}.accorder`)}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={enCours}
                      onClick={() => {
                        setMessage(null);
                        gestes.refuserSupport.mutate(demande.id, options);
                      }}
                    >
                      {t(`${cle}.refuser`)}
                    </Button>
                  </div>
                </article>
              );
            })}

            {accesSupport.map((acces) => (
              <article
                key={acces.id}
                className={styles.actif}
                aria-labelledby={`acces-${acces.id}`}
              >
                <h3 id={`acces-${acces.id}`} className={styles.titreBloc}>
                  {t(`${cle}.support.enCours`)}
                  <Badge tone="available">{t(`${cle}.etatsMandat.actif`)}</Badge>
                </h3>
                <dl className={styles.details}>
                  <div>
                    <dt>{t(`${cle}.support.personne`)}</dt>
                    <dd>{acces.supportEmail ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.support.role`)}</dt>
                    <dd>{t(`${cle}.support.roles.${acces.role}`)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.echeance`)}</dt>
                    <dd>{date(acces.expireLe)}</dd>
                  </div>
                  <div>
                    <dt>{t(`${cle}.mandat.motif`)}</dt>
                    <dd>{acces.motif}</dd>
                  </div>
                </dl>
              </article>
            ))}
            {accesSupport.length > 0 ? (
              <div className={styles.actions}>
                <Button
                  variant="danger"
                  disabled={enCours}
                  onClick={() => setAConfirmer({ type: 'revoquerSupport' })}
                >
                  {t(`${cle}.support.retirer`)}
                </Button>
              </div>
            ) : null}

            {demandesSupport.length === 0 && accesSupport.length === 0 ? (
              <p className={styles.vide}>{t(`${cle}.support.aucun`)}</p>
            ) : null}

            {historiqueSupport.length > 0 ? (
              <section aria-labelledby="support-historique">
                <h3 id="support-historique" className={styles.titreBloc}>
                  {t(`${cle}.historique`)}
                </h3>
                <ul className={styles.historique}>
                  {historiqueSupport.map((ligne) => (
                    <li key={ligne.id}>
                      <Badge tone={TON_ETAT[ligne.etat] ?? 'neutral'}>
                        {t(
                          ligne.etat === 'en_attente'
                            ? `${cle}.etatsMandat.expire`
                            : `${cle}.support.etats.${ligne.etat}`,
                        )}
                      </Badge>
                      <span>{ligne.demandeurEmail ?? '—'}</span>
                      <span className={styles.discret}>{jour(ligne.demandeLe)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </section>

      <Dialog
        open={aConfirmer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setAConfirmer(null);
        }}
        title={aConfirmer ? t(`${cle}.confirmation.${aConfirmer.type}.titre`) : ''}
        closeLabel={t(`${cle}.annuler`)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAConfirmer(null)}>
              {t(`${cle}.annuler`)}
            </Button>
            <Button
              variant={
                aConfirmer?.type === 'revoquerMandat' || aConfirmer?.type === 'revoquerSupport'
                  ? 'danger'
                  : 'primary'
              }
              onClick={() => {
                if (aConfirmer) executer(aConfirmer);
                setAConfirmer(null);
              }}
            >
              {aConfirmer ? t(`${cle}.confirmation.${aConfirmer.type}.action`) : ''}
            </Button>
          </>
        }
      >
        {aConfirmer?.type === 'accorderMandat' ? (
          <p>
            {t(`${cle}.confirmation.accorderMandat.texte`, {
              natures: natures(aConfirmer.demande.natures),
              count: aConfirmer.demande.dureeJours ?? 0,
            })}
          </p>
        ) : aConfirmer?.type === 'accorderSupport' ? (
          <p>
            {t(`${cle}.confirmation.accorderSupport.texte`, {
              email: aConfirmer.demande.demandeurEmail ?? '—',
              duree: t(`${cle}.support.heures`, { count: aConfirmer.dureeHeures }),
            })}
          </p>
        ) : aConfirmer ? (
          <p>{t(`${cle}.confirmation.${aConfirmer.type}.texte`)}</p>
        ) : null}
      </Dialog>
      <StepUpDialog
        open={aRejouer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setARejouer(null);
        }}
        onVerifie={() => {
          const geste = aRejouer;
          setARejouer(null);
          if (geste) executer(geste);
        }}
        description={t(`${cle}.elevation`)}
      />
    </div>
  );
}
